import React from 'react';
import classnames from 'classnames';
import { useLocation } from 'react-router';
import { useDeps } from 'src/hooks/useDeps';
import { Schema } from 'src/components/Schema/Schema';
import { breadcrumbSchemaId } from 'src/constants/schema';
import { currentUrl } from 'src/util/currentUrl';

import classes from './Breadcrumbs.scss';

type Props = {
    categoryUrl?: string;
    category: string;
    subcategory?: string;
    indentation?: boolean;
    isBundleBreadcrumbs?: boolean;
    breadcrumbLabel?: string;
    categoryBreadCrumbLabel?: string;
    productSubCategory?: {
        link: string;
        label: string;
    };
    urlKey?: string;
    black?: boolean;
    withSchema?: boolean;
};

export const Breadcrumbs: React.FC<Props> = ({
    categoryUrl,
    category,
    subcategory,
    indentation = false,
    isBundleBreadcrumbs = false,
    breadcrumbLabel,
    categoryBreadCrumbLabel,
    productSubCategory,
    urlKey,
    black = false,
    withSchema = true,
}) => {
    const { pathname } = useLocation();
    const { DOMAIN } = useDeps().env;

    const reorderAndCleanSubcategory = (subcategory: string) => {
        const cleanedStr = subcategory
            .replace(/\s*\/\s*/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        let words = cleanedStr.split(' ');
        const hasProducts = words.some((word) => word.toLowerCase() === 'products');
        const hasAll = words.some((word) => word.toLowerCase() === 'all');

        if (hasProducts && hasAll) {
            words = words.filter((word) => word.toLowerCase() !== 'all' && word.toLowerCase() !== 'products');
            words.push('All', 'Products');
        }

        return words.join(' ');
    };

    const computedSubcategory = reorderAndCleanSubcategory(subcategory || '');
    const brand = categoryUrl === 'brands' ? urlKey?.split('/')[1] : undefined;
    const brandName = brand && computedSubcategory?.slice(0, brand.length);
    const brandNameCategory = brand && computedSubcategory?.slice(brand.length + 1);
    const domain = DOMAIN || 'www.workwearexpress.com';
    const absoluteUrl = (path: string) => {
        const normalisedPath = path.startsWith('/') ? path : `/${path}`;

        if (normalisedPath === '/') {
            return `https://${domain}/`;
        }

        return `https://${domain}${normalisedPath}`.replace(/\/$/, '');
    };
    const currentPageUrl = absoluteUrl(pathname || '/');
    const categoryLabel =
        categoryBreadCrumbLabel && categoryBreadCrumbLabel !== '' ? categoryBreadCrumbLabel : category;
    const currentLabel = subcategory
        ? breadcrumbLabel && breadcrumbLabel !== ''
            ? breadcrumbLabel
            : computedSubcategory
        : breadcrumbLabel && breadcrumbLabel !== ''
          ? breadcrumbLabel
          : category;

    const brandNameCategoryLabel = breadcrumbLabel && breadcrumbLabel !== '' ? breadcrumbLabel : brandNameCategory;
    const renderBreadcrumbItem = (label: string, href: string, key = href) => (
        <li key={key}>
            <a href={href}>
                <span>{label}</span>
            </a>
        </li>
    );

    const items = brand
        ? [
              categoryUrl
                  ? {
                        label: categoryLabel,
                        href: absoluteUrl(categoryUrl),
                    }
                  : undefined,
              {
                  label: `${brandName} Workwear`,
                  href: absoluteUrl(`/brands/${brand}`),
              },
              brandNameCategory
                  ? {
                        label: brandNameCategoryLabel,
                        href: currentPageUrl,
                    }
                  : undefined,
          ].filter((item): item is { label: string; href: string } => Boolean(item))
        : [
              subcategory && categoryUrl
                  ? {
                        label: categoryLabel,
                        href: absoluteUrl(categoryUrl),
                    }
                  : undefined,
              subcategory && productSubCategory
                  ? {
                        label: productSubCategory.label,
                        href: absoluteUrl(productSubCategory.link),
                    }
                  : undefined,
              {
                  label: currentLabel,
                  href: subcategory || !categoryUrl ? currentPageUrl : absoluteUrl(categoryUrl),
              },
          ].filter((item): item is { label: string; href: string } => Boolean(item?.label));

    const breadcrumbList = [{ label: 'Home', href: absoluteUrl('/') }, ...items];
    const canonicalUrl = currentUrl(domain, pathname || '/');

    return (
        <nav aria-label="Breadcrumb">
            <ol
                aria-label="breadcrumb"
                className={classnames(classes.productDetailBreadcrumbs, {
                    [classes.indentation]: indentation,
                    [classes.BundleBreadcrumbs]: isBundleBreadcrumbs,
                    [classes.black]: black,
                })}
            >
                {breadcrumbList.map((item, index) =>
                    renderBreadcrumbItem(item.label, item.href, `${index}-${item.href}`),
                )}
            </ol>
            {withSchema && (
                <Schema
                    input={{
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        '@id': breadcrumbSchemaId(canonicalUrl),
                        itemListElement: breadcrumbList.map((item, index) => ({
                            '@type': 'ListItem',
                            position: index + 1,
                            name: item.label,
                            item: item.href,
                        })),
                    }}
                />
            )}
        </nav>
    );
};
